import { Box } from '@mui/material';
import { btnSize, btnType } from '../../../../packages/ui/atoms/button';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Header from '../../../../components/v2/Header';
import Typography from '../../../../components/v2/Typography';
import { typography } from '../../../../components/v2/Typography/index.constant';

import SubmitDrawer from '../caqh-information/components/drawers/SubmitDrawer';
import ImportDrawer from '../caqh-information/components/drawers/ImportDrawer';

import { useStyles } from './index.styles';
import PriorityEmpty from '../../member-detail/components/PriorityEmpty';
import { SectionNames } from '../caqh-information/index.constants';
import { getCaqhData, transformData } from '../common/utils.constants';
import { CardStatus, CardType, ProfileSectionCard } from '../common/card/profile-section-card';
import { InfoCard } from '../common/card/info-card';
import { putProviderData } from '../../../../services/datalab/datalab.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';

const MalpracticeCoverage = ({ refresh, userId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showNewUpload, setShowNewUpload] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitList, setSubmitList] = useState({ filled: [], missing: [], list: [], title: undefined });
  const [malpracticeCoverageList, setMalpracticeCoverageList] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchCaqhData = async () => {
    const caqhData = await getCaqhData({ providerId: userId, type: 'insurance-information' });
    if (caqhData) {
      setMalpracticeCoverageList(caqhData);
    }
  };

  useEffect(() => {
    void fetchCaqhData();
  }, []);

  const onApprove = async id => {
    const type = 'insurance-information';
    const requestBody = {
      providerId: userId,
      data: {
        id,
        documentStatus: 'APPROVED',
      },
    };
    try {
      const response = await putProviderData(type, requestBody);
      if (response.status === 200) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Insurance approved',
          }),
        );
        void fetchCaqhData();
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }
  };

  const onUpdate = id => {
    const data = malpracticeCoverageList.find(i => i._id === id);
    const list = transformData(data?.mainData, CardType.INSURANCE);
    setShowSubmitModal(true);
    setSubmitList({
      filled: list?.filled || [],
      missing: list?.missing || [],
      list: list?.list || [],
      title: 'Malpractice Coverage',
    });
    setSelectedRecord(data);
    setEditMode(true);
    setShowSubmitModal(true);
  };

  const onFormSubmit = async (data, edit) => {
    const type = 'insurance-information';
    let fileData = {};
    if (data.fileData) {
      fileData = data.fileData;
    }
    const requestBody = {
      providerId: userId,
      data: {
        id: selectedRecord._id,
        state: data.address.state,
        policyNumber: data.policyNumber,
        carrierInsuredName: data.carrierInsuredName,
        currentEffectiveDate: data.currentEffectiveDate,
        currentExpirationDate: data.currentExpirationDate,
        ...fileData,
      },
    };
    try {
      const { data: response } = await putProviderData(type, requestBody);
      if (response.success) {
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Malpractice coverage data updated',
          }),
        );
      } else {
        setSubmitList({ filled: [], missing: [], list: [], title: undefined });
        void fetchCaqhData();
      }
    } catch (e) {
      console.warn(e);
      if (e.data) {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: e.data.error,
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: e.message || 'Something went wrong',
          }),
        );
      }
    }
    onCloseModal();
    void fetchCaqhData();
  };

  const resetInformation = () => {
    setSubmitList({ filled: [], missing: [], list: [], title: undefined });
  };

  const onCloseModal = () => {
    setSelectedRecord(null);
    setShowNewUpload(false);
    setShowSubmitModal(false);
    refresh();
    void fetchCaqhData();
    resetInformation();
  };

  return (
    <>
      <Header
        label="Malpractice coverage"
        actions={[
          {
            children: <Typography {...typography.body.normal.medium.bold}>Add new</Typography>,
            icon: 'plus',
            variant: btnType.PRIMARY,
            size: btnSize.SMALL,
            onClick: () => setShowNewUpload(true),
            className: classes.primaryBtn,
          },
        ]}
      />
      <InfoCard records={malpracticeCoverageList} />
      {malpracticeCoverageList?.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {malpracticeCoverageList?.map((insurance, index) => {
            const cardData =
              insurance?.documentStatus === CardStatus.DISCREPANCY
                ? insurance?.filesData
                : insurance?.mainData;
            const discrepancies = [];
            if (insurance?.documentStatus === CardStatus.DISCREPANCY) {
              insurance?.issues?.includes('carrierInsuredName') && discrepancies.push('name');
              insurance?.issues?.includes('policyNumber') && discrepancies.push('number');
              insurance?.issues?.includes('address.state') && discrepancies.push('states');
              insurance?.issues?.includes('currentEffectiveDate') && discrepancies.push('issueDate');
              insurance?.issues?.includes('currentExpirationDate') && discrepancies.push('expiryDate');
            }
            return (
              <ProfileSectionCard
                key={index}
                data={insurance}
                status={insurance?.documentStatus || 'INCOMPLETE'}
                name={cardData?.carrierInsuredName || ''}
                number={cardData?.policyNumber || ''}
                states={[cardData?.address?.state || '']}
                issueDate={cardData?.currentEffectiveDate || ''}
                expiryDate={cardData?.currentExpirationDate || ''}
                file={insurance?.files?.[0] || 'Missing'}
                type={CardType.INSURANCE}
                onUpdate={() => onUpdate(insurance?._id)}
                onApprove={() => onApprove(insurance?._id)}
                discrepancies={discrepancies}
              />
            );
          })}
        </Box>
      ) : (
        <PriorityEmpty
          title="Malpractice coverage will appear here once added."
          description=""
          glyph="empty-malpractice-coverage"
          glyphOrientation="portrait"
          isEmptyWithLottie
          lottieSize={128}
        />
      )}
      {showNewUpload && (
        <ImportDrawer
          open={showNewUpload}
          onClose={onCloseModal}
          type={SectionNames.INSURANCE}
          userId={userId}
        />
      )}
      {showSubmitModal && (
        <SubmitDrawer
          missedKeys={submitList.missing || []}
          filledKeys={submitList.filled}
          open={showSubmitModal}
          userId={userId}
          title="Malpractice Coverage"
          subTitle="Malpractice Coverage description"
          list={submitList.list}
          onClose={onCloseModal}
          onEdit={() => {
            setEditMode(true);
          }}
          isTabView
          editMode={editMode}
          onFormSubmit={onFormSubmit}
          selectedRecord={selectedRecord}
          fileType={SectionNames.INSURANCE}
        />
      )}
    </>
  );
};

export default MalpracticeCoverage;
